import { useSyncExternalStore, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';

import { getTourState, subscribeTourState } from '../tour-store';
import { DEMO_LABEL, getDemoSnapshot, subscribeDemoSession } from './demo-session';

/**
 * Swaps the Apollo client for the routed app only.
 *
 * This must stay BELOW AuthProvider and AppBootstrapProvider. Both call
 * useApolloClient(), and bootstrap re-runs its network-only bootstrap query when
 * the client identity changes, deriving companies, selected company and settings
 * from the result — so swapping at the root would hand the app's control plane to
 * the demo data and could route the user out of the tour entirely.
 *
 * Engagement is a PURE DERIVATION, with no effects and no state of its own:
 *
 *     engaged = a demo client exists && pathname is one of the run's demo routes
 *
 * The route set covers every demo step of the run rather than just the current
 * one, which is what makes a list → detail handoff safe: both pages are demo
 * routes, so nothing changes as the step advances and there is no window where a
 * page still painting fixture rows loses its client or its label. Leaving the set
 * disengages at commit time, and ending the tour clears the set — so a skipped
 * tour stops serving fixtures immediately rather than leaving synthetic rows
 * clickable.
 */
export function DemoApolloBoundary({ children }: { children: ReactNode }) {
  const client = useSyncExternalStore(subscribeDemoSession, getDemoSnapshot);
  const tour = useSyncExternalStore(subscribeTourState, getTourState);
  const { pathname } = useLocation();

  if (!client || !tour?.demoRoutes.includes(pathname)) {
    return <>{children}</>;
  }

  return (
    <ApolloProvider client={client}>
      {children}
      <DemoLabel />
    </ApolloProvider>
  );
}

/**
 * Rendered by the boundary itself rather than by the tour, so demo data can never
 * be on screen without the label that disowns it — including in a screenshot sent
 * to support. z-65 clears the tour scrim (z-60) and stays under the card (z-70).
 */
function DemoLabel() {
  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-4 z-[65] flex justify-center"
      role="status"
    >
      <span className="rounded-full bg-foreground px-3 py-1.5 text-xs font-semibold tracking-wide text-background shadow-lg">
        {DEMO_LABEL}
      </span>
    </div>
  );
}
